import { useEffect, useRef } from 'react';

import {
  useWebSocket,
  WebSocketMessage,
  WebSocketOptions,
} from '@/hooks/useWebSocket';
import { MAX_PATTERN_LEN, MAX_SUBS_PER_REQUEST } from '@/hooks/wsLimits';

export interface SubscriptionPattern {
  pattern: string;
  matchType: 'prefix' | 'exact';
}

interface UseRealtimeSubscriptionOptions extends WebSocketOptions {
  /** Key patterns to subscribe to */
  patterns: SubscriptionPattern[];
  /** Enable/disable the subscription */
  enabled?: boolean;
  /** Callback when a matching update is received */
  onUpdate?: (message: WebSocketMessage) => void;
  /**
   * Called when the server rejects a subscribe request (e.g. limit exceeded,
   * invalid pattern). Receives a human-readable reason string.
   */
  onError?: (reason: string) => void;
}

interface UseRealtimeSubscriptionReturn {
  isSubscribed: boolean;
  isConnected: boolean;
}

export function useRealtimeSubscription(
  options: UseRealtimeSubscriptionOptions
): UseRealtimeSubscriptionReturn {
  const { patterns, user, enabled = true, onUpdate, onError } = options;

  const { isConnected, send, subscribe } = useWebSocket({ user });
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
    onErrorRef.current = onError;
  });

  // Stable serialization of patterns for dependency tracking
  const patternsKey = JSON.stringify(
    patterns.map(p => `${p.pattern}:${p.matchType}`).sort()
  );

  // Subscribe to the WebSocket message stream and filter relevant messages
  useEffect(() => {
    if (!enabled || !isConnected) return;

    const unsubscribe = subscribe((message: WebSocketMessage) => {
      if (message.type !== 'update') return;

      const matches = patterns.some(p => {
        if (p.matchType === 'prefix') {
          return message.key.startsWith(p.pattern);
        }
        return message.key === p.pattern;
      });

      if (matches && onUpdateRef.current) {
        onUpdateRef.current(message);
      }
    });

    return unsubscribe;
  }, [enabled, isConnected, subscribe, patternsKey]);

  // Send subscribe request and capture subscription IDs for cleanup.
  // A requestId nonce correlates the response to this specific hook instance
  // so multiple concurrent subscriptions don't cross-capture each other's IDs.
  const subIdsRef = useRef<string[]>([]);
  useEffect(() => {
    if (!enabled || !isConnected || patterns.length === 0) return;

    // Dev-mode assertions: catch limit violations before sending so the
    // developer sees the issue at the call site rather than as a silent WS drop.
    if (import.meta.env.DEV) {
      if (patterns.length > MAX_SUBS_PER_REQUEST) {
        console.error(
          `useRealtimeSubscription: patterns.length (${patterns.length}) exceeds server limit of ${MAX_SUBS_PER_REQUEST}; subscribe will be rejected`
        );
      }
      for (const p of patterns) {
        if (p.pattern.length > MAX_PATTERN_LEN) {
          console.error(
            `useRealtimeSubscription: pattern "${p.pattern.slice(0, 32)}…" length (${p.pattern.length}) exceeds server limit of ${MAX_PATTERN_LEN}; subscribe will be rejected`
          );
        }
      }
    }

    const requestId = crypto.randomUUID();

    // Track whether this effect's subscribe attempt received a server response
    // (success or error). Because the backend error envelope does NOT echo the
    // requestId, we use a per-effect boolean flag: the NEXT envelope that
    // matches either a success (action === 'subscribed' && requestId matches)
    // or an error (has top-level `error` string) is attributed to this subscribe.
    //
    // SAFETY SCOPE: The settled-flag safeguard is correct for sequential
    // re-renders of the same hook instance — each cleanup runs before the next
    // effect fires, so effects are never truly concurrent within one instance.
    //
    // KNOWN LIMITATION: It is NOT sufficient for two distinct hook instances
    // mounted in the same tick (e.g. two components both calling
    // useRealtimeSubscription simultaneously) while the backend does not echo
    // requestId in error envelopes. In that scenario both instances install
    // their own settled-flag listener, and the first error envelope is consumed
    // by whichever listener happens to run first — the second instance never
    // sees it and remains incorrectly unsettled.
    //
    // PENDING FIX: A backend-developer is adding requestId echo (omitempty) to
    // error envelopes in ws-handler/main.go as part of ENG-3110. Once that
    // lands, both instances can switch to exact requestId matching on the error
    // path, eliminating the ambiguity entirely. Until then, two simultaneously-
    // mounted instances on the same connection may misattribute a single error.
    let settled = false;

    const unsubscribeListener = subscribe(
      (
        message: WebSocketMessage & {
          action?: string;
          requestId?: string;
          subscriptionIds?: string[];
          error?: string;
        }
      ) => {
        // Success path: server echoes our requestId on the subscribed envelope.
        if (
          message.action === 'subscribed' &&
          message.requestId === requestId &&
          message.subscriptionIds
        ) {
          settled = true;
          subIdsRef.current = message.subscriptionIds;
          return;
        }

        // Rejection path: the backend sends {"error": "<reason>"} with no
        // action field and no requestId echo (see ws-handler/main.go lines
        // 98–124). We tolerate unexpected shapes and only act if the message
        // has a non-empty `error` string and we haven't settled yet.
        if (!settled && typeof message.error === 'string' && message.error) {
          settled = true;
          const reason = message.error;
          console.error(
            'useRealtimeSubscription: server rejected subscribe request',
            { reason, requestId, patternCount: patterns.length }
          );
          onErrorRef.current?.(reason);
        }
      }
    );

    send({
      action: 'subscribe',
      requestId,
      subscriptions: patterns.map(p => ({
        pattern: p.pattern,
        matchType: p.matchType,
      })),
    });

    return () => {
      unsubscribeListener();
      if (subIdsRef.current.length > 0) {
        send({
          action: 'unsubscribe',
          subscriptionIds: subIdsRef.current,
        });
        subIdsRef.current = [];
      }
    };
  }, [enabled, isConnected, send, subscribe, patternsKey]);

  const isSubscribed = enabled && isConnected && patterns.length > 0;

  return { isSubscribed, isConnected };
}
